import { useEffect, useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { toast } from 'sonner'
import { ArrowLeft, Download, Eye, Users } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Bar, BarChart, LabelList, XAxis, YAxis } from 'recharts'
import {
  ChartContainer,
  ChartTooltip,
  ChartTooltipContent,
  type ChartConfig,
} from '@/shared/components/ui/chart'
import { LineChartSerie } from '@/modules/reportes-fidelizacion/components/charts/LineChartSerie'
import { useReporte } from '../hooks/useVisualizaciones'
import { visualizacionesApi } from '../api/visualizaciones.api'

// Mismo estilo que DynamicHorizontalBarChart (registros/reporte/:id), pero con
// la paleta azul del módulo (blue-500), de oscuro a claro.
const BLUE_PALETTE = [
  '#1e40af', // blue-800
  '#1d4ed8', // blue-700
  '#2563eb', // blue-600
  '#3b82f6', // blue-500
  '#60a5fa', // blue-400
  '#93c5fd', // blue-300
  '#bfdbfe', // blue-200
  '#dbeafe', // blue-100
]

// Alto de cada barra y alto visible fijo del área de distribución (idéntico en
// todas las cards). ~7 barras visibles; el resto se ve haciendo scroll.
const BAR_HEIGHT = 40
const MAX_BARRAS_VISIBLES = 7
const ALTURA_DISTRIBUCION = MAX_BARRAS_VISIBLES * BAR_HEIGHT + 20

function DistribucionBarChart({
  datos,
  formatValue,
}: {
  datos: { label: string; valor: number }[]
  formatValue: (v: number) => string
}) {
  const chartConfig: ChartConfig = { valor: { label: 'Total' } }

  const chartData = datos.map((item, index) => {
    const key = `cat-${index}`
    const maxIndex = Math.min(BLUE_PALETTE.length - 1, datos.length + 1)
    const colorIndex =
      datos.length <= 1 ? 0 : Math.round((index / (datos.length - 1)) * maxIndex)
    chartConfig[key] = { label: item.label, color: BLUE_PALETTE[colorIndex] }
    return {
      ...item,
      displayName: item.label.length > 22 ? `${item.label.slice(0, 22)}...` : item.label,
      fill: `var(--color-${key})`,
    }
  })

  // Altura real del chart (todas las barras) y altura visible fija e idéntica
  // para todas las cards; si el chart es más alto, el contenedor scrollea.
  const chartHeight = chartData.length * BAR_HEIGHT + 20

  return (
    <div
      className="overflow-y-auto overflow-x-hidden"
      style={{ height: `${ALTURA_DISTRIBUCION}px` }}
    >
      <ChartContainer
        config={chartConfig}
        style={{ height: `${chartHeight}px` }}
        className="!aspect-auto w-full max-w-full"
      >
        <BarChart accessibilityLayer data={chartData} layout="vertical" margin={{ left: 8, right: 50 }}>
          <YAxis
            dataKey="displayName"
            type="category"
            tickLine={false}
            tickMargin={10}
            axisLine={false}
            width={100}
            tick={{ fontSize: 11 }}
            interval={0}
          />
          <XAxis dataKey="valor" type="number" hide />
          <ChartTooltip
            cursor={false}
            content={
              <ChartTooltipContent
                formatter={(value, _name, item) => (
                  <div className="flex flex-col gap-0.5">
                    <span className="font-medium">{item.payload.label}</span>
                    <span className="text-muted-foreground">
                      Cantidad: {formatValue(Number(value))}
                    </span>
                  </div>
                )}
                hideLabel
              />
            }
          />
          <Bar dataKey="valor" layout="vertical" radius={5}>
            <LabelList
              dataKey="valor"
              position="right"
              className="fill-foreground"
              fontSize={12}
              formatter={(value: number) => formatValue(value)}
            />
          </Bar>
        </BarChart>
      </ChartContainer>
    </div>
  )
}

function fechaHaceDias(dias: number): string {
  const d = new Date()
  d.setDate(d.getDate() - (dias - 1))
  return d.toISOString().slice(0, 10)
}

function StatCard({
  icon: Icon,
  label,
  valor,
}: {
  icon: typeof Eye
  label: string
  valor: string | number
}) {
  return (
    <Card className="border border-gray-300 dark:border-gray-700 bg-gradient-to-r from-blue-500/15 via-blue-500/5 via-50% to-transparent dark:from-blue-800/20 dark:via-blue-900/10 dark:via-50% dark:to-transparent">
      <CardContent className="flex items-center gap-4 p-5">
        <div className="rounded-md bg-blue-500/10 p-3">
          <Icon className="h-6 w-6 text-blue-500" />
        </div>
        <div>
          <p className="text-sm text-muted-foreground">{label}</p>
          <p className="text-2xl font-bold text-black dark:text-white">{valor}</p>
        </div>
      </CardContent>
    </Card>
  )
}

export function VisualizacionDetailPage() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const seguimientoId = id ? parseInt(id, 10) : 0
  const [rangoDias, setRangoDias] = useState('30')

  const params = useMemo(
    () => ({ desde: fechaHaceDias(Number(rangoDias)), incluir_usuarios: true }),
    [rangoDias],
  )
  const { data, isLoading, error } = useReporte(seguimientoId, params)

  const [descargando, setDescargando] = useState(false)
  const handleExportar = async () => {
    setDescargando(true)
    try {
      await visualizacionesApi.descargarReporteExcel(seguimientoId, {
        desde: params.desde,
      })
      toast.success('Excel descargado')
    } catch {
      toast.error('No se pudo generar el Excel')
    } finally {
      setDescargando(false)
    }
  }

  const columnasUsuario = useMemo(() => {
    const primera = data?.usuarios?.[0]
    return primera ? Object.keys(primera) : []
  }, [data])

  const REGISTROS_POR_PAGINA = 10
  const [paginaUsuarios, setPaginaUsuarios] = useState(1)
  const totalUsuarios = data?.usuarios.length ?? 0
  const totalPaginas = Math.max(1, Math.ceil(totalUsuarios / REGISTROS_POR_PAGINA))
  const usuariosPagina = useMemo(
    () =>
      data?.usuarios.slice(
        (paginaUsuarios - 1) * REGISTROS_POR_PAGINA,
        paginaUsuarios * REGISTROS_POR_PAGINA,
      ) ?? [],
    [data, paginaUsuarios],
  )

  // Reinicia a la primera página cuando cambia el conjunto de datos.
  useEffect(() => {
    setPaginaUsuarios(1)
  }, [totalUsuarios])

  return (
    <div className="space-y-6 p-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon" onClick={() => navigate('/visualizaciones')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">
            {data?.nombre_video || (isLoading ? 'Cargando...' : `Video ${data?.id_video ?? ''}`)}
          </h1>
          {data && (
            <p className="text-sm text-muted-foreground">
              {data.base_wp} · ID {data.id_video}
            </p>
          )}
        </div>
        <Select value={rangoDias} onValueChange={setRangoDias}>
          <SelectTrigger className="w-40">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="7">Últimos 7 días</SelectItem>
            <SelectItem value="30">Últimos 30 días</SelectItem>
            <SelectItem value="90">Últimos 90 días</SelectItem>
          </SelectContent>
        </Select>
        <Button
          className="bg-teal-800 hover:bg-teal-900 text-white"
          onClick={handleExportar}
          disabled={descargando || isLoading || !data}
        >
          <Download className="h-4 w-4 mr-2" />
          {descargando ? 'Descargando...' : 'Exportar Excel'}
        </Button>
      </div>

      {error && (
        <Alert variant="destructive">
          <AlertDescription>
            No se pudo obtener el reporte de visualizaciones. Verifica la conexión a la base de datos.
          </AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <Skeleton className="h-96 w-full" />
      ) : data ? (
        <>
          <div className="grid gap-4 md:grid-cols-2">
            <StatCard
              icon={Eye}
              label="Total visualizaciones"
              valor={data.resumen.total_visualizaciones.toLocaleString('es-EC')}
            />
            <StatCard
              icon={Users}
              label="Usuarios únicos"
              valor={data.resumen.usuarios_unicos.toLocaleString('es-EC')}
            />
          </div>

          <Card className="border border-gray-300 dark:border-gray-700">
            <CardHeader>
              <CardTitle>Visualizaciones por día</CardTitle>
            </CardHeader>
            <CardContent>
              <LineChartSerie
                data={data.serie}
                colorNombre="blue"
                height={320}
                formatValue={(v) => new Intl.NumberFormat('es-EC').format(v)}
              />
            </CardContent>
          </Card>

          {data.distribuciones.filter((d) => d.datos.length > 0).length > 0 && (
            <div className="grid gap-4 lg:grid-cols-3">
              {data.distribuciones
                .filter((d) => d.datos.length > 0)
                .map((dist) => (
                  <Card key={dist.campo} className="border border-gray-300 dark:border-gray-700">
                    <CardHeader>
                      <CardTitle>{dist.campo}</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <DistribucionBarChart
                        datos={dist.datos.map((d) => ({ label: d.label, valor: d.valor }))}
                        formatValue={(v) => new Intl.NumberFormat('es-EC').format(v)}
                      />
                    </CardContent>
                  </Card>
                ))}
            </div>
          )}

          {data.usuarios.length > 0 && (
            <Card className="border border-gray-300 dark:border-gray-700">
              <CardHeader>
                <CardTitle>Detalle de visualizaciones ({data.usuarios.length})</CardTitle>
              </CardHeader>
              <CardContent className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      {columnasUsuario.map((col) => (
                        <TableHead key={col} className="capitalize">
                          {col.replace(/_/g, ' ')}
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {usuariosPagina.map((fila, idx) => (
                      <TableRow key={(paginaUsuarios - 1) * REGISTROS_POR_PAGINA + idx}>
                        {columnasUsuario.map((col) => (
                          <TableCell key={col}>{String(fila[col] ?? '—')}</TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {totalPaginas > 1 && (
                  <div className="flex items-center justify-between pt-4">
                    <p className="text-sm text-muted-foreground">
                      Página {paginaUsuarios} de {totalPaginas} · {totalUsuarios} registros
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPaginaUsuarios((p) => Math.max(1, p - 1))}
                        disabled={paginaUsuarios <= 1}
                      >
                        Anterior
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setPaginaUsuarios((p) => Math.min(totalPaginas, p + 1))}
                        disabled={paginaUsuarios >= totalPaginas}
                      >
                        Siguiente
                      </Button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </>
      ) : null}
    </div>
  )
}
